import type { RouteComponentProps } from 'react-router-dom';

import React from 'react';
import { withRouter } from 'react-router-dom';

import useClassy from '@core/hooks/useClassy';
import useRdmdOpts from '@core/hooks/useRdmdOpts';
import { useProjectStore } from '@core/store';
import classy from '@core/utils/classy';

import Icon from '@ui/Icon';
import RDMD from '@ui/RDMD';
import SmartLink from '@ui/SmartLink';

import classes from './index.module.scss';

interface ContainerProps {
  children: React.ReactNode;
  githubLink?: string | null;
  slug?: string;
  superHub?: boolean;
}

const WithSuggestEditsLink = ({ slug, superHub = false, children, githubLink }: ContainerProps) => {
  const bem = useClassy(classes, 'SuggestEdit');

  const LinkProps = superHub
    ? { href: githubLink || '', rel: 'noreferrer', target: '_blank' }
    : { to: `/edit/${slug}`, rel: 'nofollow' };

  const linkText = `Suggest Edit${superHub ? '' : 's'}`;

  return (
    <div className="row clearfix">
      <div className="col-xs-9">{children}</div>
      <div className="col-xs-3">
        <SmartLink
          {...LinkProps}
          className={classy(bem(superHub && ''), !superHub && 'suggestEdits', 'rm-SuggestEdit')}
        >
          {superHub ? (
            <Icon
              aria-hidden="true"
              className={classy(bem('-icon', '-icon_git'), 'rm-SuggestEdit-icon')}
              name="github-filled"
            />
          ) : (
            <i aria-hidden="true" className="icon icon-edit" />
          )}
          {linkText}
          {!!superHub && (
            <Icon
              aria-label="Opens in a new tab"
              className={classy(bem('-icon', '-icon_arrow'), 'rm-SuggestEdit-icon')}
              name="arrow-up-right"
            />
          )}
        </SmartLink>
      </div>
    </div>
  );
};

const NoLinks = ({ children }: ContainerProps) => (
  <div className="row clearfix">
    <div className="col-xs-9">{children}</div>
  </div>
);

interface MatchParams {
  path?: string;
}

interface HeaderProps extends RouteComponentProps<MatchParams> {
  excerpt: string;
  githubLink?: string | null;
  lang: string;
  slugUrl?: string;
  suggestedEdits: boolean;
  superHub?: boolean;
  title?: string;
}

const Header = ({
  excerpt,
  githubLink,
  lang,
  match,
  slugUrl,
  suggestedEdits,
  title,
  superHub = false,
}: HeaderProps) => {
  let Container = NoLinks;

  if (suggestedEdits && lang === 'en' && !match?.path?.endsWith('/edit')) {
    Container = WithSuggestEditsLink;
  }

  const isRmdx = useProjectStore(s => s.data.features.mdx === 'enabled');
  const rdmdOpts = useRdmdOpts();
  return (
    <header id="content-head">
      <Container githubLink={githubLink} slug={slugUrl} superHub={superHub}>
        <h1>{title}</h1>
        {!!excerpt && (
          <div className="excerpt">
            <RDMD opts={rdmdOpts} renderer={isRmdx ? 'rmdx' : 'rdmd'}>
              {excerpt}
            </RDMD>
          </div>
        )}
      </Container>
    </header>
  );
};

export default withRouter(Header);
